import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Trash2, ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { STORAGE_KEY } from "../config/siteData";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

export const AdminPanel = () => {
  const [inquiries, setInquiries] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Učitaj upite iz localStorage
  useEffect(() => {
    const loadInquiries = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setInquiries(JSON.parse(stored));
      }
    };

    loadInquiries();

    // Slušaj promjene u localStorage (za sinkronizaciju među tabovima)
    window.addEventListener("storage", loadInquiries);
    return () => window.removeEventListener("storage", loadInquiries);
  }, []);

  // Osvježi podatke
  const refreshData = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setInquiries(JSON.parse(stored));
    }
  };

  // Obriši sve upite
  const clearAllInquiries = () => {
    if (window.confirm("Jeste li sigurni da želite obrisati sve upite?")) {
      localStorage.removeItem(STORAGE_KEY);
      setInquiries([]);
      toast.success("Svi upiti su obrisani");
    }
  };

  // Obriši pojedinačni upit
  const deleteInquiry = (id) => {
    const updated = inquiries.filter((inq) => inq.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setInquiries(updated);
    toast.success("Upit je obrisan");
  };

  return (
    <section
      className="admin-section py-8 md:py-12"
      data-testid="admin-section"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Toggle Button */}
        <div className="text-center mb-6">
          <Button
            variant="outline"
            onClick={() => setIsVisible(!isVisible)}
            className="gap-2"
            data-testid="admin-toggle-button"
          >
            {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {isVisible ? "Sakrij admin panel" : "Primljeni upiti (lokalno)"}
          </Button>
        </div>

        {/* Admin Content */}
        {isVisible && (
          <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-[#e2e8f0]">
              <div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-[#0F172A]">
                  Primljeni upiti
                </h3>
                <p className="text-sm text-[#64748b]">
                  {inquiries.length} {inquiries.length === 1 ? "upit" : "upita"} spremljeno lokalno
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshData}
                  data-testid="admin-refresh-button"
                >
                  Osvježi
                </Button>
                {inquiries.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={clearAllInquiries}
                    className="gap-1"
                    data-testid="admin-clear-all-button"
                  >
                    <Trash2 className="w-4 h-4" />
                    Obriši sve
                  </Button>
                )}
              </div>
            </div>

            {/* Table */}
            {inquiries.length === 0 ? (
              <div className="p-8 text-center text-[#64748b]" data-testid="admin-empty-state">
                Nema primljenih upita.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Ime</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Dolazak</TableHead>
                      <TableHead>Odlazak</TableHead>
                      <TableHead className="text-center">Osobe</TableHead>
                      <TableHead>Primljeno</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inquiries.slice(0, isExpanded ? undefined : 5).map((inquiry) => (
                      <TableRow key={inquiry.id} data-testid={`admin-inquiry-row-${inquiry.id}`}>
                        <TableCell className="font-medium">
                          {inquiry.fullName}
                        </TableCell>
                        <TableCell>
                          <a
                            href={`mailto:${inquiry.email}`}
                            className="text-[#004E64] hover:underline"
                          >
                            {inquiry.email}
                          </a>
                        </TableCell>
                        <TableCell>{inquiry.checkIn}</TableCell>
                        <TableCell>{inquiry.checkOut}</TableCell>
                        <TableCell className="text-center">{inquiry.guests}</TableCell>
                        <TableCell className="text-[#64748b] text-sm">
                          {format(new Date(inquiry.submittedAt), "dd.MM.yyyy HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteInquiry(inquiry.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`admin-delete-inquiry-${inquiry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Show More Button */}
                {inquiries.length > 5 && (
                  <div className="p-4 text-center border-t border-[#e2e8f0]">
                    <Button
                      variant="ghost"
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="gap-1"
                      data-testid="admin-expand-button"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Prikaži manje
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Prikaži sve ({inquiries.length})
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
